/**
 * Tenant resolution for AdminPanel.
 * Fetches tenant info from SuperAdmin to get backendUrl and tenantId.
 * When REACT_APP_TENANT_INFO_API_URL is set, uses tenant-specific backend URL.
 */

const TENANT_INFO_API_URL = process.env.REACT_APP_TENANT_INFO_API_URL || "";
const DEFAULT_BASE_URL = process.env.REACT_APP_BASE_URL || "";
/** e.g. "http://api-{subdomain}.localhost:4000/api" - used when tenant has no backendUrl in SuperAdmin */
const TENANT_BACKEND_PATTERN = process.env.REACT_APP_TENANT_BACKEND_PATTERN || "";

let tenantBackendUrl = null;
let tenantId = null;
// True only when SuperAdmin answered successfully and confirmed no tenant is
// assigned to this domain (deleted tenant / never-assigned subdomain) - not
// on a network/fetch failure, so an outage doesn't wrongly show this page.
let tenantResolutionFailed = false;

function hostWithoutPort(host) {
  if (!host || typeof host !== "string") return host || "";
  const trimmed = host.trim();
  const idx = trimmed.lastIndexOf(":");
  if (idx <= 0) return trimmed;
  const after = trimmed.slice(idx + 1);
  if (/^\d+$/.test(after)) return trimmed.slice(0, idx);
  return trimmed;
}

const DEV_TENANT_OVERRIDE_KEY = "tvid.admin.devTenantDomain";

function isLocalDevHost(host) {
  return host === "localhost" || host === "127.0.0.1";
}

// Local dev has no real hostname to look up (SuperAdmin only knows real
// domains like grieftv-admin.tvidservices.com). `?tenant_domain=` on first
// load picks which tenant to resolve as, and is remembered in localStorage
// so plain http://localhost:3002/#/... keeps working after that.
function resolveHostForApi(rawHost) {
  const host = hostWithoutPort(rawHost);
  if (!isLocalDevHost(host)) return host;

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("tenant_domain");
    if (fromQuery) {
      localStorage.setItem(DEV_TENANT_OVERRIDE_KEY, fromQuery);
      return fromQuery;
    }
    return localStorage.getItem(DEV_TENANT_OVERRIDE_KEY) || host;
  } catch {
    return host;
  }
}

/**
 * Fetch tenant info from SuperAdmin by current hostname.
 * Sets tenantBackendUrl and tenantId for use by axios.
 */
export async function fetchTenantInfo() {
  tenantResolutionFailed = false;
  if (!TENANT_INFO_API_URL) return null;

  const host =
    typeof window !== "undefined" ? window.location.hostname : "";
  const hostForApi = resolveHostForApi(host);
  if (!hostForApi) return null;

  const base = TENANT_INFO_API_URL.replace(/\/$/, "");
  const url = `${base}/api/app/tenant-info`;

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { "X-Tenant-Domain": hostForApi },
      cache: "no-store",
    });
    const data = await res.json().catch(() => ({ tenant: null }));

    if (data?.tenant) {
      tenantBackendUrl = data.tenant.backendUrl || null;
      tenantId = data.tenant.id || null;
      if (tenantId) {
        localStorage.setItem("tenantId", tenantId);
      } else {
        localStorage.removeItem("tenantId");
      }
      return data.tenant;
    }

    tenantBackendUrl = null;
    tenantId = null;
    localStorage.removeItem("tenantId");
    // The lookup itself succeeded (res.ok) - SuperAdmin confirms this domain
    // has no tenant, as opposed to a network error contacting it.
    if (res.ok) {
      tenantResolutionFailed = true;
    }
    return null;
  } catch (err) {
    console.warn("Tenant info fetch failed:", err.message);
    tenantBackendUrl = null;
    tenantId = null;
    return null;
  }
}

/** True only when SuperAdmin has confirmed this domain has no assigned tenant. */
export function isTenantResolutionFailed() {
  return tenantResolutionFailed;
}

/**
 * Derive backend URL from current hostname using pattern (e.g. vistara.localhost -> api-vistara.localhost:4000/api).
 */
function deriveBackendUrlFromHostname() {
  if (!TENANT_BACKEND_PATTERN?.includes("{subdomain}")) return null;
  if (typeof window === "undefined" || !window.location?.hostname) return null;
  const host = hostWithoutPort(window.location.hostname);
  const subdomain = host?.split(".")[0] || "";
  if (!subdomain) return null;
  return TENANT_BACKEND_PATTERN.replace("{subdomain}", subdomain).replace(/\s/g, "");
}

/** Per-host cache of the last resolved tenant, written by TenantContext. */
export const TENANT_CACHE_PREFIX = "tvid.admin.tenant.";

function cachedBackendUrl() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(
      TENANT_CACHE_PREFIX + window.location.hostname
    );
    return (raw && JSON.parse(raw)?.backendUrl) || null;
  } catch {
    return null;
  }
}

/**
 * Get the base URL for API calls.
 * 1. Tenant backendUrl from SuperAdmin (if set)
 * 2. Derived from hostname via REACT_APP_TENANT_BACKEND_PATTERN (e.g. api-vistara.localhost:4000/api)
 * 3. Default REACT_APP_BASE_URL
 */
export function getApiBaseUrl() {
  const runningLocally =
    typeof window !== "undefined" && isLocalDevHost(window.location.hostname);

  // The tenant's real backendUrl (e.g. https://api-grieftv.tvidservices.com/api)
  // is for production. Locally we still want to resolve the real tenant (for
  // its id/branding) but keep hitting the local backend for API calls.
  // Until tenant-info answers, use the last resolved backendUrl: a tenant on
  // its own backend would otherwise send the page-load requests to the
  // default API, which rejects its token and logs the user out.
  const backendUrl = tenantBackendUrl || cachedBackendUrl();
  if (backendUrl && !runningLocally) {
    const base = backendUrl.replace(/\/$/, "");
    return `${base}/v1`;
  }
  const derived = deriveBackendUrlFromHostname();
  if (derived) {
    const base = derived.replace(/\/$/, "");
    return `${base}/v1`;
  }
  return DEFAULT_BASE_URL.replace(/\/$/, "");
}

export function getTenantId() {
  return tenantId || localStorage.getItem("tenantId");
}

export function isMultiTenant() {
  return !!TENANT_INFO_API_URL;
}
